import { Box, Button, HStack, Link, Portal } from '@chakra-ui/react';
import { AnalyticsConsent, NoticePanel } from '@fluenceur/ui';
import { useTranslation } from 'react-i18next';
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useProductAnalytics } from './ProductAnalyticsProvider';

export function AnalyticsPreferences() {
  const { t } = useTranslation();
  const { choice, busy, change, available } = useProductAnalytics();
  if (!available) return null;
  return (
    <AnalyticsConsent
      enabled={choice}
      busy={busy}
      onChange={change}
      labels={{
        title: t('analytics.title'),
        description: t('analytics.description'),
        allow: t('analytics.allow'),
        refuse: t('analytics.refuse'),
        withdraw: t('analytics.withdraw'),
      }}
    />
  );
}

/** Ask for the initial choice; saved choices remain accessible in account settings. */
export function AnalyticsPreferencesControl({
  placement = 'fixed',
}: {
  placement?: 'fixed' | 'inline';
}) {
  const { t } = useTranslation();
  const { choice, available, busy, change } = useProductAnalytics();
  if (!available || busy || choice !== null) return null;
  const notice = (
    <NoticePanel
      tone="neutral"
      title={t('analytics.title')}
      aria-label={t('analytics.title')}
      role="note"
      hideIndicator
      direction="column"
      align="stretch"
      actions={
        <HStack gap="2" w="full">
          <Button size="sm" variant="outline" flex="1" disabled={busy} onClick={() => change(true)}>
            {t('analytics.initial.allow')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            flex="1"
            disabled={busy}
            onClick={() => change(false)}
          >
            {t('analytics.initial.refuse')}
          </Button>
        </HStack>
      }
    >
      {t('analytics.description')}{' '}
      <Link
        href="https://www.fluenceur.com/privacy-policy"
        target="_blank"
        rel="noopener noreferrer"
      >
        {t('analytics.initial.learnMore')}
      </Link>
    </NoticePanel>
  );
  if (placement === 'inline') return notice;
  return (
    <Portal>
      <Box
        position="fixed"
        bottom="max(16px, env(safe-area-inset-bottom))"
        insetInlineStart={{ base: '4', md: '6' }}
        insetInlineEnd={{ base: '4', md: 'auto' }}
        w={{ base: 'auto', md: '360px' }}
        maxW="calc(100vw - 32px)"
        maxH="calc(100dvh - 32px)"
        overflowY="auto"
        zIndex="popover"
      >
        {notice}
      </Box>
    </Portal>
  );
}

/** Discreet access for anonymous visitors who already allowed or refused analytics. */
export function AnalyticsPreferencesLink() {
  const { t } = useTranslation();
  const { choice, available, busy } = useProductAnalytics();
  if (!available || busy || choice === null) return null;
  return (
    <>
      {' · '}
      <DialogRoot>
        <DialogTrigger asChild>
          <Button
            variant="plain"
            color="gray.500"
            fontSize="inherit"
            fontWeight="normal"
            h="auto"
            minW="0"
            p="0"
          >
            {t('analytics.preferences')}
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('analytics.preferences')}</DialogTitle>
          </DialogHeader>
          <DialogBody pb="6">
            <AnalyticsPreferences />
          </DialogBody>
          <DialogCloseTrigger />
        </DialogContent>
      </DialogRoot>
    </>
  );
}
